// "Show my products on the site" (on by default): off takes the company's products off every listing,
// their pages "not available", nothing sold — and back again. The vendor's own
// switch; a platform admin's is Aldoo's hold, with a reason for the vendor if
// one is given, which only a platform admin lifts (vendor {products_visibility}).
// While Aldoo holds it the vendor is shown the hold, not a switch they cannot use.
// Under it, the matrix of what is sold (SalesMatrix): a kind or a content type paused on its own.
import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showNotification } from "aldoo-ra/Notification"
import PubSub from "pubsub-js"
import { ChatBubbleLeftRightIcon, EnvelopeIcon } from "@heroicons/react/24/outline"
import { SetProductsVisibility } from "../AdminShared/api"
import { Field, TextArea, Section, errorMessage, dateOnly } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import SalesMatrix from "./SalesMatrix"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const SUPPORT_EMAIL = "support@aldoo.com"

/** Aldoo's reason for a hold, asked when a platform admin turns the switch on. */
function HoldDialog({ onConfirm, onClose, busy }) {
  const t = usePortalTexts(COMPANY)
  const [reason, setReason] = useState("")
  return (
    <Modal isOpen onClose={onClose} title={t`Put the catalog on hold`} className="max-w-[560px]">
      <div className="flex flex-col gap-4 p-1" data-testid="products-hold-dialog">
        <div className="text-sm text-admin_text_secondary">{t`The company's products leave the site until Aldoo brings them back. The vendor cannot turn them on again.`}</div>
        <Field label={t`Reason shown to the vendor`} hint={t`Optional. Without one, the vendor only sees that the catalog is on hold.`}>
          <TextArea value={reason} onChange={setReason} rows={4} maxLength={2000} data-testid="products-hold-reason" />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary" onClick={() => onConfirm(reason)} disabled={busy} data-testid="products-hold-confirm">
            {t`Put on hold`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/** `adminPage`: Admin → Vendors, where a platform admin lifts a hold; the vendor portal shows the hold as the vendor sees it. */
export default function ProductsVisibility({ org, canEdit, isPlatformAdmin = false, adminPage = false, onSaved }) {
  const t = usePortalTexts(COMPANY)
  const locale = useSiteLanguage() === "bg" ? "bg-BG" : "en-GB"
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  const hidden = org.productsHidden === true
  const heldByAldoo = hidden && org.productsHiddenBy === "admin"
  // Aldoo's hold is lifted by Aldoo
  const locked = !canEdit || (heldByAldoo && !isPlatformAdmin)

  const save = async (next, reason) => {
    setBusy(true)
    try {
      const res = await SetProductsVisibility(org._id, next, reason)
      const error = errorMessage(res, t`Request failed`)
      showNotification({
        message: error || (next ? t`Products hidden from the site` : t`Products back on the site`),
        className: error ? "bg-admin_error text-white" : "bg-admin_success text-white",
      })
      if (error) return
      setAsking(false)
      onSaved?.(res.org)
    } finally {
      setBusy(false)
    }
  }

  // `hide`: true takes the products off the site
  const toggle = (hide) => {
    if (locked || busy) return
    // a platform admin's hide is a hold, and may say why
    if (hide && isPlatformAdmin) return setAsking(true)
    save(!!hide)
  }

  const status = !hidden
    ? t`The products are shown on the site.`
    : heldByAldoo
      ? t("Put on hold by Aldoo on %s", dateOnly(org.productsHiddenAt, locale))
      : t("Hidden by the company on %s", dateOnly(org.productsHiddenAt, locale))
  const reason = heldByAldoo ? String(org.productsHiddenReason || "").trim() : ""

  // the vendor under Aldoo's hold: when, why, and who to ask (a platform
  // admin looking through the portal sees it so too, and lifts it on Admin → Vendors)
  if (heldByAldoo && !(isPlatformAdmin && adminPage)) {
    return (
      <Section title={t`Visibility on the site`} className="border-amber-300">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-col gap-2" data-testid="products-hold-box">
          <div className="font-semibold" data-testid="products-hidden-status">{status}</div>
          {reason && (
            <div className="whitespace-pre-line" data-testid="products-hidden-reason">
              <span className="font-semibold">{t`Reason`}: </span>
              {reason}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
            <span>{t`Contact us if you have any questions.`}</span>
            <button type="button" onClick={() => PubSub.publish("show-chat")} className="inline-flex items-center gap-1.5 font-semibold underline underline-offset-2 hover:text-amber-700" data-testid="products-hold-chat">
              <ChatBubbleLeftRightIcon className="w-4 h-4" />
              {t`Open chat`}
            </button>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1.5 underline underline-offset-2 hover:text-amber-700" data-testid="products-hold-email">
              <EnvelopeIcon className="w-4 h-4" />
              {SUPPORT_EMAIL}
            </a>
            {isPlatformAdmin && (
              <a href="/admin/vendors" className="ml-auto text-xs underline underline-offset-2 text-amber-800/80" data-testid="products-hold-admin">
                {t`Lift the hold in Admin → Vendors`}
              </a>
            )}
          </div>
        </div>
      </Section>
    )
  }

  return (
    <Section title={<span className="inline-flex items-center gap-1.5">{t`Visibility on the site`}<HelpHint helpKey="vendor.settings#visibility" /></span>} className={hidden ? "border-amber-300" : ""}>
      {/* on — the default — is shown: the switch says "visible", off takes the catalog off the site */}
      <Field label={t`Show my products on the site`} hint={t`Off: none of your products is listed, found or sold, and their pages say they are not available. Your products, orders and settings stay as they are; turn it on to bring them back.`}>
        <div className="flex items-center gap-3" data-testid="products-hidden-toggle">
          <span className={locked ? "opacity-60 cursor-not-allowed" : ""}>
            <ToggleSwitch variant="admin" isOn={!hidden} onChange={(shown) => toggle(!shown)} />
          </span>
          <span className="text-sm">{hidden ? t`Off` : t`On`}</span>
        </div>
      </Field>
      <div className={`text-sm ${hidden ? "text-amber-800" : "text-admin_text_secondary"}`} data-testid="products-hidden-status">
        {status}
      </div>
      {reason && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 whitespace-pre-line" data-testid="products-hidden-reason">
          <span className="font-semibold">{t`Reason`}: </span>
          {reason}
        </div>
      )}
      <SalesMatrix org={org} canEdit={canEdit} isPlatformAdmin={isPlatformAdmin} adminPage={adminPage} catalogOff={hidden} />
      {asking && <HoldDialog busy={busy} onClose={() => setAsking(false)} onConfirm={(reason) => save(true, reason)} />}
    </Section>
  )
}
